import React, { useCallback, useMemo, useState } from 'react';

import { ToastProvider } from '../components';
import type { NavLinkComponent } from '../components';

import type { CrmApiPort, CrmScreenKey } from './CrmApiPort';
import type { MockClaimPort } from './mockPort';
import { CRMHostProvider } from './CRMHostContext';
import type { CRMHost } from './CRMHostContext';
import { CrmScreenRouter } from './CrmScreenRouter';
import { CrmSideNav } from './CrmSideNav';
import { SCREEN_BY_PATH } from './CrmNav';

import '../styles/crm-backoffice.css';

/**
 * จุดเข้าเดียวของโมดูล — host ทั้งสองแบบ mount ตัวนี้ตัวเดียว
 *
 *   standalone  <CRMBackoffice screen={จาก react-router} api={httpCrmApi} ... />
 *   host    <CRMBackoffice screen="claim-queue" api={adapter ของ host} ... />
 *
 * ⚠️ `key={divisionId}` ที่ CrmScreenRouter สำคัญมาก ห้ามเอาออก
 *
 *    host เปลี่ยนหน่วยงานได้ตลอดเวลาจากมุมบนซ้าย และ `api` ที่ฉีดเข้ามาก็เปลี่ยนตาม
 *    แต่หน้าจอที่ mount อยู่แล้วจะ **ไม่โหลดข้อมูลใหม่เอง** เพราะ useEffect ของแต่ละหน้า
 *    ไม่ได้ผูกกับหน่วยงาน
 *
 *    ผลที่เกิดจริงถ้าไม่มีบรรทัดนี้: ผู้ใช้เห็นข้อมูลของหน่วยงานเดิมค้างอยู่
 *    แต่พอกดอนุมัติ/บันทึก คำขอจะถูกส่งไปพร้อม header division ของหน่วยงาน**ใหม่**
 *    — เขียนข้อมูลผิดหน่วยงานโดยที่ผู้ใช้ไม่รู้ตัว
 *
 *    การ remount ทั้ง subtree แก้ได้ครบทุกหน้าในครั้งเดียว และครอบคลุมหน้าที่จะเพิ่มทีหลังด้วย
 *    (ถ้าไปไล่เติม dependency ทีละ container จะมีวันที่ใครสักคนลืม)
 *    ระหว่าง remount ตัวโหลดเดิมถูกยกเลิกด้วย cleanup ของ useEffect ที่มีอยู่แล้ว
 *
 * ทุกอย่างถูกห่อด้วย <div id="crm-bo" class="crm-bo"> เสมอ เพราะ
 *   — token ทั้งหมด scope อยู่ที่ .crm-bo
 *   — `important: '.crm-bo'` ของ Tailwind ต้องการ ancestor ตัวนี้
 *   — `contain: layout` ทำให้ position:fixed ยึดกับกล่องนี้แทน viewport
 *   — id ใช้เฉพาะกับยามของคีย์ลัด (useKeyboardShortcuts) ซึ่งต้องมีตัวเดียวในหน้า
 */
export interface CRMBackofficeProps {
  /** หน้าจอที่จะแสดง — ในโหมด showNav ใช้เป็นหน้าตั้งต้นเท่านั้น */
  screen: CrmScreenKey;

  /**
   * ให้โมดูลแสดงเมนูย่อยของตัวเองและจัดการการสลับหน้าเอง
   *
   * เปิดเมื่อ host ให้พื้นที่มา "เมนูเดียว" (เช่นเมนู MeCRM ของ HIS)
   * ปิดเมื่อ host มีเมนูของตัวเองอยู่แล้ว (โหมด standalone ที่ผูกกับ URL)
   */
  showNav?: boolean;

  /** ช่องทางคุยกับ backend */
  api: CrmApiPort;

  /** ตัวตนผู้ใช้ — ใช้บันทึก audit log */
  userId: number;
  username: string;
  isSuperUser?: boolean;

  /** หน่วยงานปัจจุบัน */
  divisionId: number;

  embed?: 'ishealth' | 'standalone';
  theme?: 'innobic' | 'host' | 'neutral';

  /** 'mock' ใช้ได้เฉพาะ standalone — ต้องส่ง mock มาด้วย */
  dataMode?: 'live' | 'mock';
  mock?: MockClaimPort;
  refreshKey?: number;

  linkComponent?: NavLinkComponent;
  onNavigate?: (to: CrmScreenKey) => void;

  className?: string;
}

export const CRMBackoffice: React.FC<CRMBackofficeProps> = ({
  screen,
  api,
  userId,
  username,
  isSuperUser = false,
  divisionId,
  showNav = false,
  embed = 'standalone',
  theme = 'innobic',
  dataMode = 'live',
  mock,
  refreshKey,
  linkComponent,
  onNavigate,
  className,
}) => {
  if (!api) {
    throw new Error(
      'CRMBackoffice ต้องได้รับ prop `api` — ' +
        'โหมด standalone ใช้ createHttpCrmApi() ส่วน host ส่ง adapter ของตัวเองที่ทำตาม CrmApiPort',
    );
  }

  if (dataMode === 'mock' && !mock) {
    throw new Error('dataMode="mock" ต้องส่ง prop `mock` มาด้วย — ดู standalone/mock/');
  }

  /**
   * หน้าจอปัจจุบันในโหมดเมนูเดียว
   *
   * ⚠️ host ไม่มี URL ให้เรา (สลับเมนูด้วยแท็บ ไม่ใช่ route)
   *    สถานะจึงอยู่ในโมดูล และหายเมื่อผู้ใช้สลับไปเมนู admin อื่นแล้วกลับมา
   *    ซึ่งยอมรับได้ เพราะทุกหน้าโหลดข้อมูลใหม่เองอยู่แล้ว
   */
  const [navScreen, setNavScreen] = useState<CrmScreenKey>(screen);
  const [navCollapsed, setNavCollapsed] = useState(false);

  const current = showNav ? navScreen : screen;

  /** ลิงก์ข้ามหน้าในเนื้อหา (เช่น "ดูรายงานคะแนน") — แปลง path เป็นหน้าจอ */
  const handleNavigate = useCallback(
    (to: CrmScreenKey) => {
      if (showNav) setNavScreen(to);
      onNavigate?.(to);
    },
    [showNav, onNavigate],
  );

  const internalLink = useMemo<NavLinkComponent | undefined>(
    () =>
      showNav
        ? ({ to, children, className, style }) => (
            <button
              type="button"
              className={className}
              style={style}
              onClick={() => {
                const target = SCREEN_BY_PATH[to];
                if (target) setNavScreen(target);
              }}
            >
              {children}
            </button>
          )
        : undefined,
    [showNav],
  );

  const host = useMemo<CRMHost>(
    () => ({
      api, userId, username, isSuperUser, divisionId,
      embed, dataMode, mock, refreshKey,
      linkComponent: linkComponent ?? internalLink,
      onNavigate: handleNavigate,
    }),
    [api, userId, username, isSuperUser, divisionId, embed, dataMode, mock, refreshKey,
     linkComponent, internalLink, handleNavigate],
  );

  return (
    <div
      id="crm-bo"
      className={['crm-bo', className].filter(Boolean).join(' ')}
      data-embed={embed}
      data-crm-theme={theme}
    >
      <CRMHostProvider value={host}>
        {/* ทุกหน้าจอในงานออกแบบใช้ useToast() สำหรับปุ่ม "เลิกทำ" — ดู screens/README */}
        <ToastProvider>
          {showNav ? (
            <div className="flex h-full min-h-0 items-stretch">
              <CrmSideNav
                current={current}
                onSelect={setNavScreen}
                collapsed={navCollapsed}
                onToggleCollapsed={() => setNavCollapsed((v) => !v)}
              />
              <div className="min-w-0 flex-1 overflow-auto">
                {/* key={divisionId} — ดูเหตุผลด้านล่าง */}
                <CrmScreenRouter key={divisionId} screen={current} />
              </div>
            </div>
          ) : (
            <CrmScreenRouter key={divisionId} screen={current} />
          )}
        </ToastProvider>
      </CRMHostProvider>
    </div>
  );
};
